import { ExpandMore } from '@mui/icons-material'
import {
  Box,
  Collapse,
  Divider,
  Fade,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { MouseEvent, ReactElement, ReactNode } from 'react'
import { Children, useEffect, useRef, useState } from 'react'

import { SIDEBAR_TRANSITION_EASING } from '@/constants/layout'

const EASE_OUT_EXPO = 'cubic-bezier(0.16, 1, 0.3, 1)'
const ROTATION_TRANSITION = `transform 220ms ${EASE_OUT_EXPO}`
const COLOR_TRANSITION = `background-color 160ms ${EASE_OUT_EXPO}, color 160ms ${EASE_OUT_EXPO}`

const LABEL_FADE_OUT_MS = 120

// On expand, section headers snap to full opacity immediately and are progressively
// revealed by the widening rail. On collapse they fade out before the rail crops them.
function labelTransition(isCollapsed: boolean) {
  return isCollapsed
    ? `opacity ${LABEL_FADE_OUT_MS}ms ${SIDEBAR_TRANSITION_EASING}, transform ${LABEL_FADE_OUT_MS}ms ${SIDEBAR_TRANSITION_EASING}`
    : 'none'
}

type CollapsibleNavSectionProps = {
  children: ReactNode
  defaultExpanded?: boolean
  icon: ReactElement
  isCollapsed?: boolean
  title: string
}

function CollapsibleNavSection({
  children,
  defaultExpanded = false,
  icon,
  isCollapsed = false,
  title,
}: Readonly<CollapsibleNavSectionProps>) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)
  const isHoveringButton = useRef(false)
  const isHoveringPopper = useRef(false)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // When the rail toggles between collapsed/expanded, dismiss any open flyout and reset
  // hover tracking so a stale anchor can't re-open the Popper on the next collapse.
  useEffect(() => {
    isHoveringButton.current = false
    isHoveringPopper.current = false
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    setAnchorEl(null)
  }, [isCollapsed])

  function handleToggle() {
    if (isCollapsed) return
    setIsExpanded((prev) => !prev)
  }

  function scheduleClose() {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    closeTimeoutRef.current = setTimeout(() => {
      if (!isHoveringButton.current && !isHoveringPopper.current) {
        setAnchorEl(null)
      }
    }, 50)
  }

  function handleButtonMouseEnter(event: MouseEvent<HTMLElement>) {
    if (!isCollapsed) return
    isHoveringButton.current = true
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    setAnchorEl(event.currentTarget)
  }

  function handleButtonMouseLeave() {
    if (!isCollapsed) return
    isHoveringButton.current = false
    scheduleClose()
  }

  function handlePopperMouseEnter() {
    isHoveringPopper.current = true
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
  }

  function handlePopperMouseLeave() {
    isHoveringPopper.current = false
    scheduleClose()
  }

  function handleMenuItemClick() {
    isHoveringButton.current = false
    isHoveringPopper.current = false
    setAnchorEl(null)
  }

  const isHighlighted = isCollapsed ? isMenuOpen : false
  const showChevronRotated = !isCollapsed && isExpanded

  // Children are individually permission-gated, so falsy entries mean an empty section.
  // Hide the whole section (header + flyout) when nothing inside is visible, instead of
  // rendering a dead, clickable header (e.g. partner_administrators / customer_managers
  // seeing empty "Bidding / Trading" and "Asset Management" sections).
  if (Children.toArray(children).length === 0) {
    return null
  }

  return (
    <>
      <ListItemButton
        sx={(theme) => ({
          py: 0.5,
          pl: 2.5,
          pr: 2,
          display: 'flex',
          alignItems: 'center',
          transition: COLOR_TRANSITION,
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.045),
          },
          '&:hover .CollapsibleNavSection-icon': {
            color: isCollapsed ? theme.palette.primary.main : theme.palette.text.primary,
          },
          '&:hover .CollapsibleNavSection-title, &:hover .CollapsibleNavSection-chevron': {
            color: theme.palette.text.primary,
          },
          ...(isHighlighted && {
            backgroundColor: alpha(theme.palette.primary.main, 0.045),
            '& .CollapsibleNavSection-icon': {
              color: theme.palette.primary.main,
            },
          }),
        })}
        onClick={handleToggle}
        onMouseEnter={handleButtonMouseEnter}
        onMouseLeave={handleButtonMouseLeave}
      >
        <ListItemIcon
          className="CollapsibleNavSection-icon"
          sx={(theme) => ({
            color: alpha(theme.palette.text.secondary, 0.7),
            minWidth: 32,
            transition: COLOR_TRANSITION,
            '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
          })}
        >
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={title}
          primaryTypographyProps={{
            className: 'CollapsibleNavSection-title',
            sx: {
              fontWeight: 500,
              fontSize: '0.8125rem',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'clip',
              opacity: isCollapsed ? 0 : 1,
              transform: isCollapsed ? 'translateX(-4px)' : 'translateX(0)',
              transition: labelTransition(isCollapsed),
              '@media (prefers-reduced-motion: reduce)': {
                transition: `${COLOR_TRANSITION}`,
              },
            },
          }}
        />
        <ExpandMore
          className="CollapsibleNavSection-chevron"
          sx={{
            color: 'text.secondary',
            fontSize: '1.125rem',
            flexShrink: 0,
            opacity: isCollapsed ? 0 : 1,
            transform: `${showChevronRotated ? 'rotate(180deg)' : 'rotate(0deg)'}`,
            transition: isCollapsed
              ? `${ROTATION_TRANSITION}, color 160ms ${EASE_OUT_EXPO}, opacity ${LABEL_FADE_OUT_MS}ms ${SIDEBAR_TRANSITION_EASING}`
              : `${ROTATION_TRANSITION}, color 160ms ${EASE_OUT_EXPO}`,
            '@media (prefers-reduced-motion: reduce)': {
              transition: `transform 1ms`,
            },
          }}
        />
      </ListItemButton>

      {isCollapsed ? (
        <Popper
          transition
          anchorEl={anchorEl}
          modifiers={[{ name: 'offset', options: { offset: [0, 0] } }]}
          open={isMenuOpen}
          placement="right-start"
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} easing={{ enter: EASE_OUT_EXPO, exit: EASE_OUT_EXPO }} timeout={160}>
              <Box
                sx={(theme) => ({
                  minWidth: 208,
                  ml: 0.5,
                  bgcolor: 'background.paper',
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: `0 1px 1px ${alpha(theme.palette.text.primary, 0.04)}, 0 4px 12px ${alpha(theme.palette.text.primary, 0.06)}, 0 16px 32px ${alpha(theme.palette.text.primary, 0.08)}`,
                  overflow: 'hidden',
                })}
                onMouseEnter={handlePopperMouseEnter}
                onMouseLeave={handlePopperMouseLeave}
              >
                <Typography
                  sx={{
                    px: 2,
                    pt: 1.25,
                    pb: 0.75,
                    fontWeight: 600,
                    fontSize: '0.6875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                  }}
                >
                  {title}
                </Typography>
                <Divider />
                <List disablePadding component="nav" sx={{ py: 0.5 }} onClick={handleMenuItemClick}>
                  {children}
                </List>
              </Box>
            </Fade>
          )}
        </Popper>
      ) : (
        <Collapse unmountOnExit in={isExpanded} timeout={220}>
          {/* pl 4 = section header text-column origin (header pl 2.5 + icon minWidth 32px). */}
          <List disablePadding component="div" sx={{ pl: 4, pb: 0.5 }}>
            {children}
          </List>
        </Collapse>
      )}
    </>
  )
}

export default CollapsibleNavSection
